import { useOptionalAnalytics } from "@/src/sb/components/Analytics"
import { ReactNode, useEffect, useRef, useState } from "react"

import StepProgress from "./StepProgress"
import TeamStep, { EMPTY_TEAM_SELECTION, TeamSelection } from "./TeamStep"
import UseCaseStep, {
  EMPTY_USE_CASE_SELECTION,
  UseCaseSelection
} from "./UseCaseStep"
import { buildProfileCompletionAttributes } from "./onboardingAttributes"
import {
  DRAFT_STORAGE_PREFIX,
  useProfileSetupDraft
} from "./useProfileSetupDraft"

export const COMPLETION_TOTAL_STEPS = 2

export type ProfileCompletionData = TeamSelection & UseCaseSelection

const EMPTY_PROFILE_COMPLETION: ProfileCompletionData = {
  ...EMPTY_TEAM_SELECTION,
  ...EMPTY_USE_CASE_SELECTION
}

type StepCopy = {
  title: string
  subtitle?: string
}

const STEP_COPY: Record<number, StepCopy> = {
  1: { title: "What team are you on?" },
  2: {
    title: "What do you currently use Stears for?",
    subtitle:
      "This helps us make sure you get exactly what you need. Select all that apply."
  }
}

export const COMPLETION_STEP_PANEL_CLASSES: Record<number, string> = {
  1: "max-w-[430px] md:min-h-[232px] overflow-visible",
  2: "max-w-[616px] md:min-h-[392px] overflow-y-auto"
}

type ProfileCompletionWizardProps = {
  onComplete: (data: ProfileCompletionData) => void | Promise<void>
  onBack: () => void
  onStepChange?: (step: number) => void
  storageKey?: string
}

const ProfileCompletionWizard = ({
  onComplete,
  onBack,
  onStepChange,
  storageKey = DRAFT_STORAGE_PREFIX
}: ProfileCompletionWizardProps) => {
  const analytics = useOptionalAnalytics()
  const { draft, restored, save, clear } =
    useProfileSetupDraft<ProfileCompletionData>(storageKey)
  const startReported = useRef(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [data, setData] = useState<ProfileCompletionData>(
    EMPTY_PROFILE_COMPLETION
  )
  const [hydrated, setHydrated] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!restored) return
    if (draft) setData({ ...EMPTY_PROFILE_COMPLETION, ...draft.data })
    setHydrated(true)
  }, [restored, draft])

  const goToStep = (step: number) => {
    setCurrentStep(step)
    onStepChange?.(step)
  }

  useEffect(() => {
    if (!hydrated) return
    save({ step: currentStep, data })
  }, [hydrated, currentStep, data, save])

  useEffect(() => {
    if (!hydrated || startReported.current) return
    startReported.current = true
    analytics?.trackOnboardingStart({
      flow: "profile_completion",
      step: currentStep,
      resumed: Boolean(draft)
    })
  }, [hydrated, currentStep, draft, analytics])

  const collect = (values: Partial<ProfileCompletionData>) =>
    setData((current) => ({ ...current, ...values }))

  const goToNextStep = async (values: Partial<ProfileCompletionData>) => {
    const updated = { ...data, ...values }
    setData(updated)

    if (currentStep === COMPLETION_TOTAL_STEPS) {
      setSubmitting(true)
      try {
        await onComplete(updated)
        analytics?.trackOnboardingComplete({
          flow: "profile_completion",
          attributes: buildProfileCompletionAttributes(updated)
        })
        clear()
      } catch {
        setSubmitting(false)
      }
      return
    }

    goToStep(currentStep + 1)
  }

  const goToPreviousStep = () =>
    currentStep === 1 ? onBack() : goToStep(currentStep - 1)

  const steps: Record<number, ReactNode> = {
    1: (
      <TeamStep
        initialValues={data}
        onSubmit={goToNextStep}
        onBack={goToPreviousStep}
        onChange={collect}
      />
    ),
    2: (
      <UseCaseStep
        initialValues={data}
        onSubmit={goToNextStep}
        onBack={goToPreviousStep}
        onChange={collect}
        submitLabel="Submit"
        submitting={submitting}
        actionsAlign="end"
      />
    )
  }

  const { title, subtitle } = STEP_COPY[currentStep]

  if (!hydrated) return null

  return (
    <section className="flex w-full flex-col gap-5 text-left font-sans">
      <StepProgress
        currentStep={currentStep}
        totalSteps={COMPLETION_TOTAL_STEPS}
        trackClassName="bg-[#0E0F10]"
      />

      <header className="flex flex-col gap-2">
        <h1 className="font-jakarta text-sm font-bold leading-[22px] dark:text-stears_white">
          {title}
        </h1>
        {subtitle ? (
          <p className="text-xs tracking-[-0.01em] dark:text-stears_white">
            {subtitle}
          </p>
        ) : null}
      </header>

      <main>{steps[currentStep]}</main>
    </section>
  )
}

export default ProfileCompletionWizard
